import { DOMNode, domToReact, Element } from 'html-react-parser';
import { Sentry } from 'lib-shared-sentry';

import {
  matchesQuoteStyle,
  QUOTE_CONTAINER_SELECTORS,
  QUOTE_SIBLING_SELECTORS,
  QUOTE_START_SELECTORS,
  SANITIZED_OUTLOOK_HR_STYLES,
  SANITIZED_OUTLOOK_QUOTE_STYLES,
} from 'utils/emailQuotes';

import {
  TestFunction,
  TestFunctionsConfig,
} from '../HTMLShadowDomRenderer.types';
import { EmailReplyQuote } from '../components/emailReplyQuote/emailReplyQuote';

import { hasSomePreviousSiblingPassingTest } from './siblingGroupCache';

// Most quotes will occur fairly high up in the DOM tree, testing show 10-20 is enough
// to catch most quotes. If we don't find a quote by then, we can assume there is no quote and stop searching.
// Sibling breadth (many flat siblings, not deep nesting) is a separate axis,
// bounded by the memoized hasSomePreviousSiblingPassingTest instead of a cap.
const MAX_DEPTH = 40;

const containerSelectors = parseSelectors(QUOTE_CONTAINER_SELECTORS);
const startSelectors = parseSelectors(QUOTE_START_SELECTORS);
const siblingSelectors = parseSelectors(QUOTE_SIBLING_SELECTORS);

/**
 * Contains the configuration for test functions used in replace function. Refer
 * to usage examples in the type definition and add new quote styles to
 * `utils/emailQuotes`, shared with the transcript builder. Each group is a
 * single test function, as every one of them runs for all nodes.
 */
const testFunctionsConfig: TestFunctionsConfig = {
  children: [(domNode) => matchesSomeSelector(domNode, containerSelectors)],
  nextSiblings: [
    (domNode) => matchesSomeSelector(domNode, startSelectors),
    (domNode) =>
      isTagName(domNode, 'hr') &&
      hasStyle(domNode, SANITIZED_OUTLOOK_HR_STYLES),
    (domNode) => hasStyle(domNode, SANITIZED_OUTLOOK_QUOTE_STYLES),
    (domNode) =>
      hasSomeChildPassingTest(
        domNode,
        (childNode) =>
          hasStyle(childNode, SANITIZED_OUTLOOK_QUOTE_STYLES) &&
          childNode.parent?.children.filter((node) => isElement(node))
            .length === 1,
      ),
  ],
  allSiblings: [(domNode) => matchesSomeSelector(domNode, siblingSelectors)],
};

const getRootNodeDistance = (domNode: DOMNode): number => {
  let rootNode = domNode;
  let distance = 0;
  while (rootNode.parent && distance <= MAX_DEPTH) {
    rootNode = rootNode.parent;
    distance++;
  }

  return distance;
};

let sentryCalled = false;

/**
 * Replaces a node with quote if certain conditions are met.
 *
 * @param domNode The node to be processed
 * @warn This function runs once for each node in the DOM tree. It is very slow
 * and should be used with caution.
 */
export function replaceReplyQuote(domNode: DOMNode): JSX.Element | DOMNode {
  if (!isElement(domNode)) {
    return domNode;
  }

  // if distance to root element is greater than MAX_DEPTH, return the node
  if (getRootNodeDistance(domNode) > MAX_DEPTH) {
    // Call sentry once, no matter how many times this function is called
    if (!sentryCalled) {
      sentryCalled = true;
      Sentry.captureMessage('replaceReplyQuote: MAX_DEPTH reached');
    }

    return domNode;
  }

  /**
   * If a node has a previous sibling passing "next siblings" test functions, it
   * means that it has already been moved to the quote section. So, it should be
   * removed it in order to prevent duplicate renders.
   */
  if (
    testFunctionsConfig.nextSiblings.some((test) =>
      hasSomePreviousSiblingPassingTest(domNode, test),
    )
  ) {
    return <></>;
  }

  /**
   * If a node passes "next siblings" test functions, it should be moved with
   * its all next siblings into quote section.
   */
  if (testFunctionsConfig.nextSiblings.some((test) => test(domNode))) {
    return (
      <EmailReplyQuote>{domToReact(getNextSiblings(domNode))}</EmailReplyQuote>
    );
  }

  /**
   * If a node passes "children" test functions, its children should be moved to
   * quote section.
   *
   * Also, if a node has some child passing "all siblings" test function, its
   * children should be moved to quote section.
   */
  if (
    testFunctionsConfig.children.some((test) => test(domNode)) ||
    testFunctionsConfig.allSiblings.some((test) =>
      hasSomeChildPassingTest(domNode, test),
    )
  ) {
    return <EmailReplyQuote>{domToReact(domNode.children)}</EmailReplyQuote>;
  }

  return domNode;
}

/* Checks if a node is of type Element */
function isElement(domNode: DOMNode): domNode is Element {
  return 'attribs' in domNode;
}

/** Checks if a node contains one of the given styles. */
function hasStyle(
  domNode: DOMNode,
  styles: readonly string[],
): domNode is Element {
  return isElement(domNode) && matchesQuoteStyle(domNode.attribs.style, styles);
}

/** Checks a node contains the given class. */
function hasClass(domNode: DOMNode, className: string): domNode is Element {
  return (
    isElement(domNode) &&
    domNode.attribs.class?.split(/\s+/).includes(className)
  );
}

/** Checks if a node has the given name. */
function isTagName(domNode: DOMNode, tagName: string): domNode is Element {
  return isElement(domNode) && domNode.name === tagName;
}

/** A `tag`, `.class` or `tag.class` selector, split up ahead of matching. */
type ParsedSelector = {
  tagName: string | undefined;
  className: string | undefined;
};

/**
 * Splits `tag`, `.class` and `tag.class` selectors. Selectors that name neither
 * are dropped, as they would match every node.
 */
function parseSelectors(selectors: readonly string[]): ParsedSelector[] {
  return selectors
    .map((selector) => {
      const [tagName, className] = selector.split('.');

      return {
        tagName: tagName || undefined,
        className: className || undefined,
      };
    })
    .filter(({ tagName, className }) => tagName ?? className);
}

/** Checks if a node matches one of the given selectors. */
function matchesSomeSelector(
  domNode: DOMNode,
  selectors: readonly ParsedSelector[],
): boolean {
  return selectors.some(
    ({ tagName, className }) =>
      (!tagName || isTagName(domNode, tagName)) &&
      (!className || hasClass(domNode, className)),
  );
}

/** Checks if a node contains a child passing test function. */
function hasSomeChildPassingTest(
  domNode: DOMNode,
  test: TestFunction,
): domNode is Element {
  return isElement(domNode) && domNode.children.some(test);
}

/** Returns next siblings (inclusive) of a given node */
function getNextSiblings(domNode: DOMNode): DOMNode[] {
  const nextSiblings: DOMNode[] = [domNode];
  for (
    let nextSibling = domNode.nextSibling;
    nextSibling;
    nextSibling = nextSibling?.nextSibling
  ) {
    nextSiblings.push(nextSibling);
  }

  return nextSiblings;
}
